import React, { useState } from 'react';
import { useGetJoinRequestsQuery, useLazyGetJoinRequestsQuery, useDeleteJoinRequestMutation } from '@/store/api/communityApi';
import { Loader2, Search, ChevronLeft, ChevronRight, Trash2, Download } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader } from '@/components/common/Loader';
import { ErrorBanner } from '@/components/common/ErrorBanner';
import { Modal } from '@/components/common/Modal';
import { useNotifications } from '@/hooks/useNotifications';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useDebounce } from '@/hooks/useDebounce';

export function JoinRequestsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [page, setPage] = useState(1);
  const [take, setTake] = useState(10);
  const skip = (page - 1) * take;

  const [deleteRequest, { isLoading: isDeleting }] = useDeleteJoinRequestMutation();
  const [requestToDelete, setRequestToDelete] = useState<{ id: number; name: string } | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [selectedDescription, setSelectedDescription] = useState<{ name: string; description: string } | null>(null);
  const { success: notifySuccess, error: notifyError } = useNotifications();

  const [fetchForExport, { isFetching: isExporting }] = useLazyGetJoinRequestsQuery();

  const {
    data: response,
    isLoading,
    error,
    isFetching,
  } = useGetJoinRequestsQuery({
    search: debouncedSearch,
    skip,
    take,
  });

  // Reset page when search or take changes
  React.useEffect(() => {
    setPage(1);
  }, [debouncedSearch, take]);

  const confirmDelete = async () => {
    if (!requestToDelete || !deleteReason.trim()) return;
    try {
      await deleteRequest({ id: requestToDelete.id, reason: deleteReason.trim() }).unwrap();
      notifySuccess('Join request deleted successfully.');
      setRequestToDelete(null);
      setDeleteReason('');
    } catch (err: any) {
      notifyError(err?.data?.message || 'Failed to delete join request. Please try again.');
    }
  };

  const handleExportCSV = async () => {
    try {
      const result = await fetchForExport({
        skip: 0,
        take: 1000,
        search: debouncedSearch,
      }).unwrap();

      if (!result.data || result.data.length === 0) {
        notifyError('No data available to export.');
        return;
      }

      const headers = ['Name', 'Email', 'Mobile', 'Description', 'Date'];
      const csvRows = [headers.join(',')];

      result.data.forEach((req) => {
        const row = [
          `"${req.firstname} ${req.lastname}"`,
          `"${req.email.replace(/"/g, '""')}"`,
          `"${(req.mobile || '').replace(/"/g, '""')}"`,
          `"${(req.description || '').replace(/"/g, '""')}"`,
          `"${new Date(req.createdAt).toLocaleDateString()}"`,
        ];
        csvRows.push(row.join(','));
      });

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `community_requests_${new Date().getTime()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: any) {
      notifyError(err?.data?.message || 'Failed to export CSV. Please try again.');
    }
  };

  const totalPages = response ? Math.ceil(response.count / take) : 0;
  const requests = response?.data || [];

  if (isLoading && !requests.length) {
    return <Loader />;
  }

  if (error) {
    return (
      <ErrorBanner
        type="error"
        title="Failed to load requests"
        message="There was an error loading the community join requests."
      />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Join Requests</h1>
            <p className="text-muted-foreground mt-1">
              People who have requested to join the OCCSS community.
            </p>
          </div>
          {requests.length > 0 && (
            <Button onClick={handleExportCSV} disabled={isExporting} className="w-full sm:w-auto">
              {isExporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Export CSV
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0 pb-4">
          <div>
            <CardTitle>Recent Requests</CardTitle>
            <CardDescription>A list of all users who submitted the join form.</CardDescription>
          </div>
          <div className="flex flex-col sm:flex-row flex-wrap items-center gap-2 mt-4 sm:mt-0 w-full sm:w-auto">
            {/* Items per page filter */}
            <select
              value={take}
              onChange={(e) => setTake(Number(e.target.value))}
              className="h-9 w-full sm:w-auto rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search name, email..."
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="relative">
            {isFetching && (
              <div className="absolute inset-0 bg-background/50 z-10 flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            )}

            {requests.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs text-muted-foreground uppercase bg-muted/50 border-b">
                    <tr>
                      <th className="px-4 py-3 font-medium">Name</th>
                      <th className="px-4 py-3 font-medium">Email</th>
                      <th className="px-4 py-3 font-medium">Mobile</th>
                      <th className="px-4 py-3 font-medium">Description</th>
                      <th className="px-4 py-3 font-medium">Date</th>
                      <th className="px-4 py-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {requests.map((request) => (
                      <tr key={request.id} className="hover:bg-muted/50 transition-colors">
                        <td className="px-4 py-4 font-medium text-foreground">
                          {request.firstname} {request.lastname}
                        </td>
                        <td className="px-4 py-4">{request.email}</td>
                        <td className="px-4 py-4">{request.mobile}</td>
                        <td className="px-4 py-4">
                          {request.description ? (
                            <button
                              onClick={() => setSelectedDescription({ name: `${request.firstname} ${request.lastname}`, description: request.description! })}
                              className="text-left max-w-[250px] truncate hover:text-blue-600 transition-colors focus:outline-none"
                              title="Click to view full description"
                            >
                              {request.description}
                            </button>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-muted-foreground">
                          {new Date(request.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() =>
                                setRequestToDelete({
                                  id: request.id,
                                  name: `${request.firstname} ${request.lastname}`,
                                })
                              }
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">No join requests found.</div>
            )}
          </div>

          {/* Pagination Controls */}
          {response && response.count > 0 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-muted-foreground">
                Showing <span className="font-medium">{skip + 1}</span> to{' '}
                <span className="font-medium">{Math.min(skip + take, response.count)}</span> of{' '}
                <span className="font-medium">{response.count}</span> results
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <div className="text-sm font-medium">
                  Page {page} of {totalPages}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Modal
        isOpen={!!requestToDelete}
        onClose={() => {
          if (!isDeleting) {
            setRequestToDelete(null);
            setDeleteReason('');
          }
        }}
        title="Delete Join Request"
      >
        <div className="space-y-4 pt-2 text-sm">
          <p className="text-muted-foreground">
            Are you sure you want to delete the join request from{' '}
            <span className="font-semibold text-foreground">{requestToDelete?.name}</span>? This
            action cannot be undone.
          </p>
          <div className="space-y-2">
            <label className="font-medium">
              Reason for Deletion <span className="text-destructive">*</span>
            </label>
            <p className="text-xs text-muted-foreground">
              This reason will be included in the email sent to the user.
            </p>
            <textarea
              className="w-full min-h-[100px] p-3 rounded-md border border-input bg-background shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
              placeholder="e.g. We are currently not accepting new members..."
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              disabled={isDeleting}
            />
          </div>
        </div>
        <div className="mt-6 flex flex-col sm:flex-row-reverse gap-3 border-t pt-4">
          <Button
            variant="destructive"
            onClick={confirmDelete}
            disabled={isDeleting || !deleteReason.trim()}
            className="w-full sm:w-auto"
          >
            {isDeleting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Deleting...
              </>
            ) : (
              'Confirm Delete & Send Email'
            )}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setRequestToDelete(null);
              setDeleteReason('');
            }}
            disabled={isDeleting}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={!!selectedDescription}
        onClose={() => setSelectedDescription(null)}
        title={`Description: ${selectedDescription?.name}`}
      >
        <div className="pt-2 text-sm text-foreground whitespace-pre-wrap leading-relaxed max-h-[60vh] overflow-y-auto">
          {selectedDescription?.description}
        </div>
        <div className="mt-6 flex justify-end border-t pt-4">
          <Button variant="outline" onClick={() => setSelectedDescription(null)}>
            Close
          </Button>
        </div>
      </Modal>
    </div>
  );
}
